import Header from '@/components/Header';
import { useAuth } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SeverityChip } from '@/components/SeverityChip';
import { subscribeWebPush, unsubscribeWebPush, isPushSupported } from '@/lib/webpush';
import { Save, Radio, Mail, Zap, MessageSquare, ExternalLink, XCircle, KeyRound, Loader2, AlertTriangle, Truck, CloudRain, Waves } from 'lucide-react';
import { RoutesManager } from '@/components/RoutesManager';

export default function Settings() {
  const { user, setUser, refreshMe } = useAuth();
  const [params, setParams] = useSearchParams();
  const [highways, setHighways] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    alert_threshold: user?.alert_threshold || 'yellow',
    subscribed_highways: user?.subscribed_highways || [],
    channels: user?.channels || { portal: true, webpush: false, email: true, sms: false },
    unplanned_only: user?.unplanned_only || false,
    weather_alerts: user?.weather_alerts !== false,
    is_load_pilot: user?.is_load_pilot || false,
    pilot_class: user?.pilot_class || 'Class 1',
    pilot_number: user?.pilot_number || '',
    company: user?.company || '',
    quake_alerts: user?.quake_alerts || false,
    quake_radius_km: user?.quake_radius_km || 30,
  });
  const [saving, setSaving] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState('');
  const [planSeats, setPlanSeats] = useState(3);
  const [planBusy, setPlanBusy] = useState('');
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialSms, setTrialSms] = useState(false);
  const [trialPhone, setTrialPhone] = useState(user?.phone || '');

  useEffect(() => {
    api.get('/highways').then((r) => setHighways(r.data));
    api.get('/sms-tiers').then((r) => setTiers(r.data));
  }, []);

  // Handle Stripe redirect return
  useEffect(() => {
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkout === 'success' && sessionId) {
      (async () => {
        try {
          const { data } = await api.post('/billing/verify-session', { session_id: sessionId });
          if (data.activated) {
            if (data.plan) toast.success(`${data.plan === 'plus' ? 'Plus' : 'Business'} plan activated!`);
            else toast.success(`SMS activated for ${data.island === 'north' ? 'North' : 'South'} Island!`);
            await refreshMe();
          } else {
            toast.info('Payment received, activation pending…');
          }
        } catch { toast.error('Could not verify checkout'); }
        setParams({}, { replace: true });
      })();
    } else if (checkout === 'cancel') {
      toast.info('Checkout cancelled');
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const toggleHighway = (key) => {
    const s = new Set(form.subscribed_highways);
    if (s.has(key)) s.delete(key); else s.add(key);
    setForm({ ...form, subscribed_highways: Array.from(s) });
  };

  const selectAllIsland = (islandKey) => {
    const list = (highways?.[islandKey] || []).map((h) => h.key);
    const s = new Set(form.subscribed_highways);
    list.forEach((k) => s.add(k));
    setForm({ ...form, subscribed_highways: Array.from(s) });
  };
  const clearIsland = (islandKey) => {
    const remove = new Set((highways?.[islandKey] || []).map((h) => h.key));
    setForm({ ...form, subscribed_highways: form.subscribed_highways.filter((k) => !remove.has(k)) });
  };

  const setChannel = (k, v) => setForm({ ...form, channels: { ...form.channels, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const wasPilot = user?.is_load_pilot && user?.pilot_status === 'approved';
      const { data } = await api.patch('/auth/me', form);
      setUser(data);
      localStorage.setItem('ptt_user', JSON.stringify(data));
      if (data.is_load_pilot && data.pilot_status === 'pending' && !wasPilot) {
        toast.success('Saved — your Load Pilot request was sent for admin approval.');
      } else {
        toast.success('Settings saved');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (!(await isPushSupported())) {
        // Special guidance for iOS Safari
        const ua = navigator.userAgent || '';
        const isIos = /iPad|iPhone|iPod/.test(ua);
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
        if (isIos && !isStandalone) {
          toast.error('On iPhone: tap Share → Add to Home Screen first, then open PTT NZ Alerts from your Home Screen and enable Web Push here.', { duration: 8000 });
        } else if (isIos && isStandalone) {
          toast.error('Web Push requires iOS 16.4 or later. Please update iOS and try again.', { duration: 6000 });
        } else {
          toast.error('Web push not supported on this browser');
        }
        setPushBusy(false); return;
      }
      if (form.channels.webpush) {
        await unsubscribeWebPush();
        setChannel('webpush', false);
        toast.success('Web push disabled');
      } else {
        await subscribeWebPush();
        setChannel('webpush', true);
        toast.success('Web push enabled – you\'ll receive alerts even when the app is closed');
      }
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      toast.error(e.message || 'Push toggle failed');
    } finally { setPushBusy(false); }
  };

  const startPlanCheckout = async (plan) => {
    setPlanBusy(plan);
    try {
      const { data } = await api.post('/billing/subscribe', { plan, seats: planSeats });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout'); setPlanBusy(''); }
  };
  const cancelPlan = async () => {
    if (!window.confirm('Cancel your plan subscription? Premium features stay until the end of the billing period.')) return;
    try { await api.post('/billing/cancel-plan'); toast.success('Plan cancelled'); await refreshMe(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  const startTrial = async () => {
    setTrialBusy(true);
    try {
      const { data } = await api.post('/trial/start', { include_sms: trialSms, phone: trialSms ? trialPhone : undefined });
      if (data.user) setUser(data.user);
      toast.success(`Free trial started — ${data.includes_sms ? 'Plus features + SMS' : 'Plus features'} unlocked for 7 days!`);
      await refreshMe();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start trial'); }
    finally { setTrialBusy(false); }
  };
  const trialDaysLeft = user?.trial_ends_at ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000)) : 0;

  const startCheckout = async (island) => {
    if (!form.phone) {
      toast.error('Please add your phone number and save before subscribing to SMS');
      return;
    }
    setCheckoutBusy(island);
    try {
      const { data } = await api.post('/billing/checkout', { island });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
      setCheckoutBusy('');
    }
  };

  const cancelSms = async (island) => {
    const label = island === 'north' ? 'North Island' : 'South Island';
    if (!window.confirm(`Cancel your ${label} SMS subscription? You'll lose SMS alerts for ${label} highways at the end of the billing period.`)) return;
    try {
      await api.post('/billing/cancel', { island });
      toast.success(`${label} subscription cancelled`);
      await refreshMe();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cancel failed'); }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post('/billing/portal');
      window.location.href = data.url;
    } catch { toast.error('Could not open billing portal'); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-display font-black text-3xl md:text-4xl mb-2">Settings</h1>
        <p className="text-zinc-600 mb-8">Manage your account, alert thresholds and notification channels.</p>

        {/* Profile */}
        <Section title="Profile">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="label">Full name</label><input className="input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="settings-name" /></div>
            <div><label className="label">Email</label><input className="input" value={user?.email} disabled /></div>
            <div><label className="label">Phone (E.164 for SMS, e.g., +64211234567)</label><input className="input" placeholder="+64 21 000 0000" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone" /></div>
          </div>
        </Section>

        {/* Alert Threshold */}
        <Section title="Alert Threshold" subtitle="Choose the minimum severity you want to hear about. Selections are cumulative.">
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { v: 'red', label: 'Critical only', desc: 'Road closures only', color: '#E11D48' },
              { v: 'orange', label: 'Urgent + Critical', desc: 'Crashes, hazards, closures', color: '#F7941D' },
              { v: 'yellow', label: 'All (Info + above)', desc: 'Roadworks, weather & everything else', color: '#FBBF24' },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setForm({ ...form, alert_threshold: o.v })}
                className={`text-left p-4 rounded-xl border-2 transition ${form.alert_threshold === o.v ? 'shadow-md' : 'hover:border-zinc-300'}`}
                style={{
                  borderColor: form.alert_threshold === o.v ? o.color : '#e4e4e7',
                  background: form.alert_threshold === o.v ? `${o.color}0f` : '#fff',
                }}
                data-testid={`threshold-${o.v}`}
              >
                <SeverityChip severity={o.v} outline />
                <div className="font-display font-bold text-lg mt-3">{o.label}</div>
                <div className="text-sm text-zinc-600">{o.desc}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* Highways */}
        <Section title="Highway Subscriptions" subtitle="Follow specific state highways per island. SH1 in the North Island and SH1 in the South Island are separate — subscribe to what you actually travel.">
          <div className="grid md:grid-cols-2 gap-6" data-testid="settings-highways">
            {['north', 'south'].map((islandKey) => {
              const list = highways?.[islandKey] || [];
              const emoji = islandKey === 'north' ? '🌋' : '🏔️';
              const label = islandKey === 'north' ? 'North Island' : 'South Island';
              return (
                <div key={islandKey} className="p-4 rounded-xl border border-zinc-200 bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{emoji}</span>
                      <div>
                        <div className="font-display font-bold">{label}</div>
                        <div className="text-[10px] text-zinc-500 tracking-wider uppercase">{list.length} highways · {list.reduce((s, x) => s + (x.count || 0), 0)} events</div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => selectAllIsland(islandKey)} className="text-[10px] uppercase tracking-wider font-semibold text-teal-600 hover:text-teal-800 px-2 py-1 rounded hover:bg-teal-50" data-testid={`select-all-${islandKey}`}>All</button>
                      <button type="button" onClick={() => clearIsland(islandKey)} className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded hover:bg-zinc-100" data-testid={`clear-${islandKey}`}>None</button>
                    </div>
                  </div>
                  {list.length === 0 ? (
                    <div className="text-xs text-zinc-400 py-4 text-center">No active events</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {list.map((h) => {
                        const on = form.subscribed_highways.includes(h.key);
                        return (
                          <button
                            key={h.key}
                            type="button"
                            onClick={() => toggleHighway(h.key)}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition inline-flex items-center gap-1.5 ${on ? 'bg-teal-500 border-teal-500 text-black' : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-400'}`}
                            data-testid={`hw-${h.key}`}
                            title={`${h.highway} · ${label} · ${h.count} events`}
                          >
                            {h.highway}
                            <span className={`text-[10px] px-1.5 rounded-full ${on ? 'bg-black/20' : 'bg-zinc-100'}`}>{h.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {form.subscribed_highways.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="text-zinc-500">Following <strong className="text-zinc-900">{form.subscribed_highways.length}</strong> highway{form.subscribed_highways.length !== 1 ? 's' : ''}.</span>
              <button type="button" onClick={() => setForm({ ...form, subscribed_highways: [] })} className="text-xs text-zinc-500 hover:text-zinc-800 underline">Clear all</button>
            </div>
          )}
        </Section>

        {user?.role !== 'admin' && !(user?.org_id && user?.org_role !== 'owner') && (
        <Section title="Plan & Billing" subtitle="Upgrade to unlock Route Watch (Plus) or freight bridge restrictions & team seats (Business).">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-sm">Current plan:</span>
            <span className="chip chip-outline-orange uppercase" data-testid="current-plan">{user?.trial_active ? 'plus · trial' : (user?.plan || 'free')}</span>
          </div>
          {user?.trial_active ? (
            <div className="mb-4 p-4 rounded-xl border-2 border-teal-300 bg-teal-50/60" data-testid="trial-active-banner">
              <div className="font-display font-bold text-teal-800 flex items-center gap-2"><Zap className="w-4 h-4" /> Free Plus trial active</div>
              <p className="text-sm text-teal-700 mt-1">{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left{user?.trial_includes_sms ? ' · includes free SMS alerts' : ''}. Choose a plan below to keep these features when your trial ends.</p>
            </div>
          ) : (!user?.trial_used && (user?.plan || 'free') === 'free') ? (
            <div className="mb-4 p-4 rounded-xl border-2 border-orange-200 bg-orange-50/60" data-testid="trial-offer">
              <div className="font-display font-bold text-lg">Try Plus free for 7 days</div>
              <p className="text-sm text-zinc-600 mt-1 mb-3">Unlock Route/Corridor Watch, unlimited highways, email &amp; web push — no card required.</p>
              <label className="flex items-start gap-2 text-sm mb-3">
                <input type="checkbox" className="mt-1" checked={trialSms} onChange={(e) => setTrialSms(e.target.checked)} data-testid="trial-include-sms" />
                <span>Also trial paid <strong>SMS alerts</strong> free — critical road &amp; weather warnings by text (limited to one free SMS trial per mobile number).</span>
              </label>
              {trialSms && (
                <input className="input mb-3" placeholder="Mobile number e.g. 021 234 5678" value={trialPhone} onChange={(e) => setTrialPhone(e.target.value)} data-testid="trial-phone" />
              )}
              <button className="btn btn-primary" disabled={trialBusy} onClick={startTrial} data-testid="start-trial-btn">{trialBusy ? 'Starting…' : 'Start free trial'}</button>
            </div>
          ) : (user?.trial_used && (user?.plan || 'free') === 'free') ? (
            <div className="mb-4 p-3 rounded-xl border border-zinc-200 bg-zinc-50 text-sm text-zinc-600" data-testid="trial-ended">Your free trial has ended. Choose a plan below to continue with Plus features.</div>
          ) : null}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border-2 border-zinc-200">
              <div className="font-display font-bold text-lg">Plus</div>
              <div className="text-2xl font-black">$4.00<span className="text-sm font-normal text-zinc-500">/mo</span></div>
              <p className="text-sm text-zinc-500 my-2">Route/Corridor Watch, unlimited highways, email &amp; web push.</p>
              <button className="btn btn-primary w-full" disabled={planBusy === 'plus' || user?.plan === 'plus'} onClick={() => startPlanCheckout('plus')} data-testid="subscribe-plus">{user?.plan === 'plus' ? 'Current plan' : (planBusy === 'plus' ? 'Redirecting…' : 'Choose Plus')}</button>
            </div>
            <div className="p-4 rounded-xl border-2 border-teal-300 bg-teal-50/40">
              <div className="font-display font-bold text-lg">Business <span className="text-xs font-normal text-zinc-500">(Transport &amp; Load Pilots)</span></div>
              <div className="text-2xl font-black">$5.50<span className="text-sm font-normal text-zinc-500">/seat/mo</span></div>
              <p className="text-sm text-zinc-500 my-2">Everything in Plus + freight bridge restrictions &amp; team seats.</p>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm">Seats</label>
                <input type="number" min="2" className="input w-24" value={planSeats} onChange={(e) => setPlanSeats(parseInt(e.target.value) || 2)} data-testid="business-seats" />
                <span className="text-sm text-zinc-500">= ${(planSeats * 5.5).toFixed(2)}/mo</span>
              </div>
              <button className="btn btn-primary w-full" disabled={planBusy === 'business' || user?.plan === 'business'} onClick={() => startPlanCheckout('business')} data-testid="subscribe-business">{user?.plan === 'business' ? 'Current plan' : (planBusy === 'business' ? 'Redirecting…' : 'Choose Business')}</button>
            </div>
          </div>
          {user?.plan !== 'free' && (
            <div className="mt-4 flex gap-2">
              <button className="btn btn-ghost" onClick={openPortal} data-testid="manage-billing">Manage billing</button>
              <button className="btn btn-ghost text-red-600" onClick={cancelPlan} data-testid="cancel-plan">Cancel plan</button>
            </div>
          )}
        </Section>
        )}

        {/* Routes */}
        <RoutesManager highways={highways} locked={!(user?.role === 'admin' || ['plus', 'business'].includes(user?.plan))} />

        {/* Channels */}
        <Section title="Notification Channels">
          <div className="space-y-3">
            <ChannelRow icon={<Radio className="w-4 h-4" />} label="In-portal alerts" desc="Always shown in your notifications inbox." checked={form.channels.portal !== false} onChange={(v) => setChannel('portal', v)} testid="ch-portal" />
            <ChannelRow icon={<Zap className="w-4 h-4" />} label="Web Push notifications" desc="Get browser/PWA notifications, even when the tab is closed. Works on Android + desktop; iOS requires 'Add to Home Screen'." checked={!!form.channels.webpush} onChange={togglePush} disabled={pushBusy} testid="ch-webpush" />
            <ChannelRow icon={<Mail className="w-4 h-4" />} label="Email" desc={`Sent via SMTP from ${user?.email ? 'alerts@pttnz.net' : ''}. Free for all users.`} checked={form.channels.email !== false} onChange={(v) => setChannel('email', v)} testid="ch-email" />

            <ChannelRow icon={<Mail className="w-4 h-4" />} label="Product news & newsletters" desc="Occasional updates about new features, service news and tips from PTT NZ Alerts. This is separate from your road-alert emails — turning it off never affects your safety alerts." checked={!form.marketing_opt_out} onChange={(v) => setForm({ ...form, marketing_opt_out: !v })} testid="ch-marketing" />

            <ChannelRow icon={<AlertTriangle className="w-4 h-4" />} label="Unplanned events only" desc="Cut the noise from planned roadworks. When on, you're only alerted for unplanned events — crashes, slips, flooding, hazards and unplanned closures — across every channel (in-portal, push, email and SMS)." checked={!!form.unplanned_only} onChange={(v) => setForm({ ...form, unplanned_only: v })} testid="ch-unplanned-only" />

            <ChannelRow icon={<CloudRain className="w-4 h-4" />} label="MetService weather warnings" desc="Get MetService severe-weather warnings — road snowfall, strong wind and heavy rain — for the island your subscribed highways are on. Colour-coded Yellow/Orange/Red and filtered by your alert threshold. Turn off to receive road events only." checked={form.weather_alerts !== false} onChange={(v) => setForm({ ...form, weather_alerts: v })} testid="ch-weather-alerts" />

            <ChannelRow icon={<Truck className="w-4 h-4" />} label="I'm a Load Pilot" desc="Free. Unlocks the Load Planner so you can schedule oversize/overweight loads, auto-build corridors and coordinate head-on meets. New pilots are reviewed by an admin before access is granted." checked={!!form.is_load_pilot} onChange={(v) => setForm({ ...form, is_load_pilot: v })} testid="ch-load-pilot" />

            {form.is_load_pilot && (
              <div className="rounded-xl border-2 border-teal-100 bg-teal-50/40 p-4 space-y-4" data-testid="settings-pilot-fields">
                {user?.is_load_pilot && user?.pilot_status && (
                  <div data-testid="pilot-status-badge">
                    {user.pilot_status === 'approved' && <span className="chip" style={{ background: '#dcfce7', color: '#166534' }}>✓ Approved Load Pilot</span>}
                    {user.pilot_status === 'pending' && <span className="chip" style={{ background: '#fef9c3', color: '#854d0e' }}>⏳ Awaiting admin approval</span>}
                    {user.pilot_status === 'rejected' && <span className="chip" style={{ background: '#fee2e2', color: '#991b1b' }}>Application not approved</span>}
                  </div>
                )}
                <div>
                  <label className="label">Pilot class</label>
                  <select className="input" value={form.pilot_class} onChange={(e) => setForm({ ...form, pilot_class: e.target.value })} data-testid="settings-pilot-class">
                    <option value="Class 1">Class 1 Pilot</option>
                    <option value="Class 2">Class 2 Pilot</option>
                  </select>
                </div>
                <div>
                  <label className="label">Pilot number</label>
                  <input className="input" value={form.pilot_number} onChange={(e) => setForm({ ...form, pilot_number: e.target.value })} placeholder="Your NZTA pilot certificate number" data-testid="settings-pilot-number" />
                </div>
                <div>
                  <label className="label">Company <span className="text-zinc-400 font-normal">(optional)</span></label>
                  <input className="input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Your pilot/transport company" data-testid="settings-company" />
                </div>
              </div>
            )}

            {/* SMS Tier Section – per-island */}
            <div className="rounded-xl border-2 border-zinc-100 p-4 bg-gradient-to-br from-teal-50 to-orange-50/30">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare className="w-4 h-4" />
                <div className="flex-1">
                  <div className="font-display font-bold">SMS Alerts</div>
                  <div className="text-xs text-zinc-600">Paid per island. Subscribe to one, the other, or both — flat monthly fee covers all alert levels for the highways you follow on that island.</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {['north', 'south'].map((islandKey) => {
                  const t = tiers.find((x) => x.tier === islandKey);
                  const emoji = islandKey === 'north' ? '🌋' : '🏔️';
                  const label = islandKey === 'north' ? 'North Island' : 'South Island';
                  const sub = user?.sms_subscriptions?.[islandKey] || {};
                  const active = !!sub.active;
                  return (
                    <div
                      key={islandKey}
                      className={`p-4 rounded-xl border-2 transition ${active ? 'border-teal-400 bg-teal-50/50' : 'border-zinc-200 bg-white'}`}
                      data-testid={`sms-island-card-${islandKey}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{emoji}</span>
                          <div>
                            <div className="font-display font-bold">{label}</div>
                            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500">SMS · all alert levels</div>
                          </div>
                        </div>
                        {active && <span className="chip chip-outline-orange" data-testid={`sms-active-${islandKey}`}>Active</span>}
                      </div>
                      <div className="text-xs text-zinc-600 mb-3 min-h-[32px]">
                        {t ? t.description : 'Loading…'}
                      </div>
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="font-display font-black text-2xl">NZ${t ? Number(t.price_nzd).toFixed(2) : '—'}</span>
                        <span className="text-xs text-zinc-500">/month</span>
                      </div>
                      {active ? (
                        <button
                          type="button"
                          onClick={() => cancelSms(islandKey)}
                          className="btn btn-ghost w-full text-red-600"
                          data-testid={`sms-cancel-${islandKey}`}
                        >
                          <XCircle className="w-4 h-4" /> Cancel
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startCheckout(islandKey)}
                          disabled={checkoutBusy === islandKey || !t?.active}
                          className="btn btn-primary w-full"
                          data-testid={`sms-subscribe-${islandKey}`}
                        >
                          {checkoutBusy === islandKey ? 'Redirecting…' : t?.active === false ? 'Not available' : `Subscribe to ${label}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {(user?.sms_subscriptions?.north?.active || user?.sms_subscriptions?.south?.active) && user?.stripe_customer_id && (
                <div className="mt-3">
                  <button type="button" onClick={openPortal} className="btn btn-ghost text-sm" data-testid="sms-portal">
                    <ExternalLink className="w-4 h-4" /> Manage billing on Stripe
                  </button>
                </div>
              )}

              <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                SMS is sent to your saved phone number for alerts on your subscribed highways on the island(s) you pay for.
              </p>
            </div>
          </div>
        </Section>

        <div className="sticky bottom-4 z-10 mt-8">
          <button onClick={save} className="btn btn-primary w-full md:w-auto shadow-lg" disabled={saving} data-testid="settings-save">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        <ChangePasswordSection />
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error('New password must be at least 6 characters');
    if (form.new_password !== form.confirm) return toast.error('New passwords do not match');
    if (form.new_password === form.current_password) return toast.error('New password must differ from current');
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password changed');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Change failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="card mt-6">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-orange-600" />
        <h2 className="font-display font-bold text-xl">Change Password</h2>
      </div>
      <form onSubmit={submit} className="grid md:grid-cols-3 gap-4" data-testid="change-password-form">
        <div>
          <label className="label">Current password</label>
          <input type="password" required className="input" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="cp-current" />
        </div>
        <div>
          <label className="label">New password</label>
          <input type="password" required minLength={6} className="input" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="cp-new" />
        </div>
        <div>
          <label className="label">Confirm new password</label>
          <input type="password" required minLength={6} className="input" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="cp-confirm" />
        </div>
        <div className="md:col-span-3">
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="cp-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="card mb-6">
      <h2 className="font-display font-bold text-xl mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-zinc-500 mb-4">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </div>
  );
}

function ChannelRow({ icon, label, desc, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border-2 transition cursor-pointer ${checked ? 'border-teal-400 bg-teal-50/40' : 'border-zinc-100 hover:border-zinc-200'} ${disabled ? 'opacity-60' : ''}`}>
      <div className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-700 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="flex-1">
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-5 h-5 accent-teal-500" data-testid={testid} />
    </label>
  );
}
